import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus} from 'platform/components';
import {Lightbox} from 'platform/lightbox';

import {isNil, isNotNil} from 'ramda';

import {useReadCodeQuery} from '@dms/api/core';
import {useGetRentalOrderQuery} from '@dms/api/rental';
import {useGetVehicleV2Query} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetRentalVehicleQuery} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  EntityHeader,
  EntityHeaderFlag,
  getRentalOrderStateFlag,
  useVehicleHeaderImage,
  useGetRentalOrderActions,
} from '@dms/shared';

import {TestIdProps} from 'shared/web';

import {useRentalOrderHeaderParameters} from './hooks/useRentalOrderHeaderParameters';
import {useRentalOrderHeaderSnippets} from './hooks/useRentalOrderHeaderSnippets';
import {useRentalOrderTotalPrice} from './hooks/useRentalOrderTotalPrice';

interface RentalOrderHeaderProps extends Required<TestIdProps> {
  rentalOrderId: string | undefined;
}

export function RentalOrderHeader(props: RentalOrderHeaderProps) {
  const isCreating = isNil(props.rentalOrderId);

  const {
    data: rentalOrder,
    isError: isRentalOrderError,
    isLoading: isRentalOrderLoading,
  } = useGetRentalOrderQuery(
    {rentalOrderId: props.rentalOrderId ?? ''},
    {skip: !props.rentalOrderId}
  );

  const cancellationReasonId = rentalOrder?.cancellationReasonId;
  const {data: cancellationReason} = useReadCodeQuery(
    isNotNil(cancellationReasonId)
      ? {codelistId: 'cancellationReason', codeId: cancellationReasonId}
      : skipToken
  );

  const vehicleId = rentalOrder?.rentalVehicleId;

  const {
    data: vehicle,
    isError: isVehicleError,
    isLoading: isVehicleLoading,
  } = useGetVehicleV2Query(
    {vehicleId: vehicleId ?? ''},
    {
      skip: !vehicleId,
    }
  );
  const {
    data: rentalVehicle,
    isError: isRentalVehicleError,
    isLoading: isRentalVehicleLoading,
  } = useGetRentalVehicleQuery(
    {vehicleId: vehicleId ?? ''},
    {
      skip: !vehicleId,
    }
  );

  const [lightboxControls, coverPhoto, onCoverPhotoOpen, vehicleImages, photoDownloadSizeDialog] =
    useVehicleHeaderImage(vehicle?.id);

  const parameters = useRentalOrderHeaderParameters(vehicle, rentalOrder, rentalVehicle);

  const actions = useGetRentalOrderActions(rentalOrder);
  const {snippets, areSnippetsLoading} = useRentalOrderHeaderSnippets(props.rentalOrderId);
  const {primaryParameter, secondaryParameter} = useRentalOrderTotalPrice(
    rentalOrder,
    rentalVehicle
  );
  const cancellationReasonName =
    rentalOrder?.state === 'canceled' ? cancellationReason?.name : undefined;
  const cancellationReasonFlag: EntityHeaderFlag | null = isNotNil(cancellationReasonName)
    ? {colorScheme: 'red', label: cancellationReasonName, isSubtle: true}
    : null;
  const flags = [getRentalOrderStateFlag(rentalOrder?.state), cancellationReasonFlag].filter(
    isNotNil
  );
  const title = isCreating
    ? i18n.t('page.rentalOrder.labels.new')
    : `${rentalOrder?.number} - ${vehicle?.title}`;

  const isError = isRentalOrderError || isVehicleError || isRentalVehicleError;

  return (
    <DataStatus isError={isError} minHeight={67}>
      <EntityHeader
        data-testid={props['data-testid']}
        title={title}
        icon={isNil(coverPhoto) ? 'sidebar/rental-order' : undefined}
        image={coverPhoto}
        actions={actions}
        onImageClick={coverPhoto ? onCoverPhotoOpen : undefined}
        recordId={props.rentalOrderId}
        resourceId={EntityResourceIds.rentalOrder}
        enableTeams={isFeatureEnabled(featureFlags.CORE_TEAMS)}
        parameters={parameters}
        primaryParameter={primaryParameter}
        secondaryParameter={secondaryParameter}
        snippets={snippets}
        isEntityLoading={isRentalOrderLoading || isVehicleLoading || isRentalVehicleLoading}
        isLoadingSnippets={areSnippetsLoading}
        flags={flags}
        permissions={{
          tag: {
            isReadable: true,
            isCreatable: true,
            isUpdatable: true,
            isDeletable: true,
            isAssignable: true,
            isUnassignable: true,
          },
        }}
        controls={['RENTAL_VEHICLE', 'ASSIGNEE', 'RENTAL_BRANCH']}
      />

      <Lightbox
        data-testid={testIds.vehicles.detail('header')}
        controls={lightboxControls}
        images={vehicleImages}
      />
      {photoDownloadSizeDialog}
    </DataStatus>
  );
}
